<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import type { SessionInfo } from '../types';
  import { createEventDispatcher } from 'svelte';
  import { generateUUID } from '../lib/utils/uuid';
  import { trackEvent } from '../lib/apiService';
  import CollapsibleSelect from './CollapsibleSelect.svelte';
  import { openDialogWithSlideUp, closeDialogWithSlideDown } from '../lib/utils/dialogAnimations';
  const dispatch = createEventDispatcher();
  
  export let sessionInfo: SessionInfo;
  export let myClub = null;
  export let myTeamName = $_('scoreBoard.myTeam');
  export let opponentClub = null;
  export let opponentTeamName = $_('scoreBoard.opponent');
  export let teamScore = 0;
  export let location = 'home';
  export let opponentScore = 0;
  export let highlights = [];  // Array of MatchHighlight objects
  
  
  // Define highlight types and their actions
  const highlightTypes = [
    { id: 'goal', label: $_('scoreBoard.highlights.goal'), icon: sessionInfo.active_team.club.sport === 'hockey' ? '🏑' : '⚽', hidden: true, actions: ['goal-scorer', 'assist'] },
    { id: 'opponent-goal', label: $_('scoreBoard.highlights.opponentGoal'), icon: '❌', hidden: true, actions: [] },
    // { id: 'pass', label: $_('scoreBoard.highlights.pass'), icon: '↗️', actions: ['pass'] },
    { id: 'great-action', label: $_('scoreBoard.highlights.greatAction'), icon: '👏', actions: ['great-action'] },
    { id: 'great-save', label: $_('scoreBoard.highlights.greatSave'), icon: '🧤', actions: ['great-save'] },
    { id: 'substitution', label: $_('scoreBoard.highlights.substitution'), icon: '🔄', actions: ['player-in', 'player-out'] },
    { id: 'injury', label: $_('scoreBoard.highlights.injury'), icon: '❤️‍🩹', actions: ['injury'] },
    { id: 'yellow-card', label: $_('scoreBoard.highlights.yellowCard'), icon: '🟨', actions: ['yellow-card'] },
    { id: 'red-card', label: $_('scoreBoard.highlights.redCard'), icon: '🟥', actions: ['red-card'] },
    { id: 'other', label: $_('scoreBoard.highlights.other'), icon: '📝', actions: ['other'] }
  ];

  function getHighlightIcon(typeId) {
    return highlightTypes.find(type => type.id === typeId)?.icon || '📝';
  }

  function getHighlightLabel(typeId) {
    return highlightTypes.find(type => type.id === typeId)?.label || 'Other';
  }

  let showHighlightModal = false;
  let selectedHighlightType = null;
  let editingHighlight = null;
  let currentComment = ''; // Add this variable for comment binding
  let showMenuModal = false;
  let selectedHighlightForMenu = null;
  let showResetConfirmation = false;
  let highlightDialogElement: HTMLDialogElement;
  
  async function openHighlight(existingHighlight = null) {
    if (existingHighlight) {
      editingHighlight = existingHighlight;
      selectedHighlightType = highlightTypes.find(t => t.id === existingHighlight.type);
      currentComment = existingHighlight.comment || '';
    } else {
      editingHighlight = null;
      currentComment = '';
    }
    showHighlightModal = true;

    // Wait for DOM update and trigger animation
    await new Promise(resolve => setTimeout(resolve, 0));
    if (highlightDialogElement) {
      await openDialogWithSlideUp(highlightDialogElement);
    }
  }

  // Expose a function that can be called from the parent component
  export function openNewHighlight() {
    editingHighlight = null;
    openHighlight();
  }

  function openMenuModal(highlight) {
    selectedHighlightForMenu = highlight;
    showMenuModal = true;
  }

  function closeMenuModal() {
    showMenuModal = false;
    selectedHighlightForMenu = null;
  }

  function selectHighlightType(type) {
    selectedHighlightType = type;
    
    if (editingHighlight) {
      // Update the existing highlight with the new type
      const index = highlights.findIndex(h => h.id === editingHighlight.id);
      if (index !== -1) {
        highlights[index] = {
          ...editingHighlight,
          type: type.id,
          label: type.label,
          actions: [] // Clear existing actions since they might not be valid for new type
        };
        editingHighlight = highlights[index];
        highlights = [...highlights];
      }
    } else {
      const isGoal = type.id === 'goal' || type.id === 'opponent-goal';
      const newHighlight = {
        id: generateUUID(),
        index: getNextHighlightIndex(),
        type: type.id,
        label: type.label,
        actions: [],
        ...(isGoal && {
          goal_index: type.id === 'goal' ? teamScore + 1 : opponentScore + 1,
          is_opponent_goal: type.id === 'opponent-goal'
        })
      };
      editingHighlight = newHighlight;
      highlights = [...highlights, newHighlight];
      // Close the modal if no player actions are required
      if (!type.actions || type.actions.length === 0) {
        closeModals();
      }
    }
    dispatch('highlightsChange', {highlights});
  }

  function getNextHighlightIndex() {
    if (highlights.length === 0) return 0;
    return Math.max(...highlights.map(h => h.index)) + 1;
  }

  async function closeModals() {
    if (editingHighlight) {
      const index = highlights.findIndex(h => h.id === editingHighlight.id);
      if (index !== -1) {
        highlights[index] = {
          ...editingHighlight,
          comment: currentComment
        };
        highlights = [...highlights]; // Trigger reactivity
      }
    }
    dispatch('highlightsChange', {highlights});

    await closeDialogWithSlideDown(highlightDialogElement);
    showHighlightModal = false;
    selectedHighlightType = null;
    editingHighlight = null;
    currentComment = ''; // Reset comment when closing
  }

  // Helper to get unique player names from highlight actions
  function getPlayerNames(actions, type) {
    if (type === 'goal') {
      const scorer = actions.find(a => a.action_type === 'goal-scorer')?.name || '';
      const assist = actions.find(a => a.action_type === 'assist')?.name || '';
      
      if (scorer && assist) {
        return `${scorer} (${assist})`;
      } else if (scorer) {
        return scorer;
      } else if (assist) {
        return `(${assist})`;
      }
      return `<ion-icon name='help-outline'></ion-icon>`;
    }
    
    // For non-goal highlights, keep the existing behavior
    const names = actions
      .map(action => action.name)
      .filter((name, index, self) => self.indexOf(name) === index);
    return names.join(', ');
  }

  function isPlayerInHighlight(player, highlight, action_type) {
    if (!highlight) return false;
    return highlight.actions.some(action =>
      action.name === player.name && action.action_type === action_type
    );
  }

  // Get selected player name for a specific action type
  function getSelectedPlayer(highlight, action_type) {
    if (!highlight) return null;
    const action = highlight.actions.find(a => a.action_type === action_type);
    return action ? action.name : null;
  }

  // Handle player selection from CollapsibleSelect
  function handlePlayerSelect(event, action) {
    const { option } = event.detail;

    if (editingHighlight) {
      // Update existing highlight
      const index = highlights.findIndex(h => h.id === editingHighlight.id);
      if (index !== -1) {
        // Remove existing action of this type if it exists
        const filteredActions = editingHighlight.actions.filter(a => a.action_type !== action);
        // Add new action
        const updatedHighlight = {
          ...editingHighlight,
          actions: [...filteredActions, {
            id: generateUUID(),
            name: option.name,
            action_type: action
          }]
        };
        highlights[index] = updatedHighlight;
        editingHighlight = updatedHighlight;
        highlights = [...highlights];
      }
    } else {
      // Create new highlight
      const newHighlight = {
        id: generateUUID(),
        index: getNextHighlightIndex(),
        type: selectedHighlightType.id,
        label: selectedHighlightType.label,
        actions: [{
          id: generateUUID(),
          name: option.name,
          action_type: action
        }]
      };
      highlights = [...highlights, newHighlight];
      editingHighlight = newHighlight;
    }
  }

  // Handle clearing player selection from CollapsibleSelect
  function handlePlayerClear(event, action) {
    if (editingHighlight) {
      const index = highlights.findIndex(h => h.id === editingHighlight.id);
      if (index !== -1) {
        // Remove action of this type
        const updatedHighlight = {
          ...editingHighlight,
          actions: editingHighlight.actions.filter(a => a.action_type !== action)
        };
        highlights[index] = updatedHighlight;
        editingHighlight = updatedHighlight;
        highlights = [...highlights];
      }
    }
  }


  function createScoreHighlight() {
    const newHighlight = {
      id: generateUUID(),
      index: getNextHighlightIndex(),
      type: 'goal',
      label: getHighlightLabel('goal'),
      actions: [],
      goal_index: teamScore + 1, // Use teamScore directly
      is_opponent_goal: false
    };
    highlights = [...highlights, newHighlight];
    // console.log('dispatching scorebord highlightsChange:', highlights);
    openHighlight(newHighlight);
    trackEvent('create_highlight', {
      module: "scoreboard",
      action_type: "create_data",
      description: 'Create highlight',
      value: 'goal'
    }, sessionInfo.user.id);
  }

  function createOpponentScoreHighlight() {
    const newHighlight = {
      id: generateUUID(),
      index: getNextHighlightIndex(),
      type: 'opponent-goal',
      label: getHighlightLabel('opponent-goal'),
      actions: [],
      goal_index: opponentScore + 1, // Use opponentScore directly
      is_opponent_goal: true
    };
    highlights = [...highlights, newHighlight];
    // console.log('dispatching scorebord highlightsChange:', highlights);
    dispatch('highlightsChange', {highlights});
    trackEvent('create_highlight', {
      module: "scoreboard",
      action_type: "create_data",
      description: 'Create highlight',
      value: 'opponent-goal'
    }, sessionInfo.user.id);
  }

  function removeLastGoalHighlight(is_opponent_goal) {
    // Find the last goal highlight of the specified type
    const goalHighlights = highlights.filter(h => 
      h.is_opponent_goal === is_opponent_goal && 
      (h.type === (is_opponent_goal ? 'opponent-goal' : 'goal'))
    );

    if (goalHighlights.length > 0) {
      // Sort by goal_index in descending order and get the last one
      const lastGoal = goalHighlights.sort((a, b) => b.goal_index - a.goal_index)[0];
      
      // Remove this highlight
      highlights = highlights.filter(h => h.id !== lastGoal.id);
      dispatch('highlightsChange', {highlights});
      trackEvent('delete_highlight', {
        module: "scoreboard",
        action_type: "delete_data",
        description: 'Delete highlight',
        value: lastGoal.type
      }, sessionInfo.user.id);
    }
  }

  function adjustTeamScore(increment) {
    console.log('adjusting team score', increment);
    const newScore = increment ? 
      Math.min(30, teamScore + 1) : 
      Math.max(0, teamScore - 1);
      
    if (increment && newScore > teamScore) {
      createScoreHighlight();
    } else if (!increment && newScore < teamScore) {
      removeLastGoalHighlight(false);
    }
    teamScore = newScore;
    //changes are already dispatched in the createScoreHighlight and removeLastGoalHighlight functions
  }

  function adjustOpponentScore(increment) {
    console.log('adjusting opponent score', increment);
    const newScore = increment ? 
      Math.min(30, opponentScore + 1) : 
      Math.max(0, opponentScore - 1);
      
    if (increment && newScore > opponentScore) {
      createOpponentScoreHighlight();
    } else if (!increment && newScore < opponentScore) {
      removeLastGoalHighlight(true);
    }
    opponentScore = newScore;
    //changes are already dispatched in the createScoreHighlight and removeLastGoalHighlight functions
  }



  function moveHighlight(direction) {
    if (!selectedHighlightForMenu) return;
    
    const currentIndex = highlights.findIndex(h => h.id === selectedHighlightForMenu.id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex >= 0 && newIndex < highlights.length) {
      const reorderedHighlights = [...highlights];
      const [movedItem] = reorderedHighlights.splice(currentIndex, 1);
      reorderedHighlights.splice(newIndex, 0, movedItem);
      
      // Update indices
      reorderedHighlights.forEach((highlight, index) => {
        highlight.index = reorderedHighlights.length - index;
      });
      
      highlights = reorderedHighlights;
      // console.log('dispatching scorebord highlightsChange 3:', highlights);
      dispatch('highlightsChange', {highlights});
    }

    trackEvent('move_highlight', {
      module: "scoreboard",
      action_type: "update_data",
      description: 'Move highlight',
      value: selectedHighlightForMenu.type
    }, sessionInfo.user.id);
    
    closeMenuModal();
  }

  function deleteHighlight() {
    if (!selectedHighlightForMenu) return;
    
    highlights = highlights.filter(h => h.id !== selectedHighlightForMenu.id);
    // console.log('dispatching scorebord highlightsChange 4:', highlights);
    dispatch('highlightsChange', {highlights});
    trackEvent('delete_highlight', {
        module: "scoreboard",
        action_type: "delete_data",
        description: 'Delete a highlight',
        value: selectedHighlightForMenu.type
      }, sessionInfo.user.id);
    closeMenuModal();
  }

  function openResetConfirmation() {
    showResetConfirmation = true;
  }

  function closeResetConfirmation() {
    showResetConfirmation = false;
  }

  function resetHighlights() {
    // Reset scores
    teamScore = 0;
    opponentScore = 0;
    
    // Clear highlights
    highlights = [];
    
    // Dispatch changes
   //console.log('dispatching scorebord highlightsChange 5:', highlights);
    dispatch('highlightsChange', {highlights});
    
    // Close confirmation dialog
    closeResetConfirmation();
  }

</script>

<div class="score-board" class:away-game={location.toLowerCase() === 'away'}>
  <div class="team-container">
    <div class="club-name">{myClub?.name}</div>
    <div class="team-logo">
      <img src={myClub?.logo} alt={myClub?.name} />
    </div>
    <div class="team-name">{myTeamName}</div>
    <div class="score-controls">
      <button
        class="secondary score-adjust"
        on:click|stopPropagation={() => adjustTeamScore(true)}
        aria-label="Increase score"
      >
        +
      </button>
      <div
        class="score-display"
      >
        {teamScore}
    </div>
      <button
        class="secondary score-adjust"
        on:click|stopPropagation={() => adjustTeamScore(false)}
        aria-label="Decrease score"
      >
        -
      </button>
    </div>
  </div>

  <div class="score-divider">-</div>

  <div class="team-container">
    <div class="club-name">{opponentClub?.name}</div>
    <div class="team-logo">
      <img src={opponentClub?.logo} alt={opponentClub?.name} />
    </div>
    <div class="team-name">{opponentTeamName}</div>
    <div class="score-controls">
      <button
        class="secondary score-adjust"
        on:click|stopPropagation={() => adjustOpponentScore(true)}
        aria-label="Increase score"
      >
        +
      </button>
      <div
        class="score-display"
      >
        {opponentScore}
    </div>
      <button
        class="secondary score-adjust"
        on:click|stopPropagation={() => adjustOpponentScore(false)}
        aria-label="Decrease score"
      >
        -
      </button>
    </div>
  </div>
</div>
  
<!-- <p class="small-text">{$_('scoreBoard.latestHighlights')}</p> -->
<div class="highlights-list">
  {#each highlights.sort((a, b) => b.index - a.index) as highlight}
  <article class="p-0">
    <div class="highlight-row">
      {#if highlight.type !== 'opponent-goal' && highlight.type !== 'goal'}
        <button
          class="highlight-menu-btn"
          on:click={() => openMenuModal(highlight)}
          aria-label="Open menu"
        >
          <ion-icon name="menu"></ion-icon>
        </button>
      {:else}
        <div class="highlight-menu-btn">
          &nbsp;
        </div>
      {/if}
      <button
        class="highlight-main-btn"
        on:click={() => openHighlight(highlight)}
      >
        <div class="highlight-content">
          <div class="highlight-header">
            <div class="highlight-left">
              <span class="highlight-icon">{getHighlightIcon(highlight.type)}</span>
              <span class="highlight-label">{getHighlightLabel(highlight.type)}</span>
              {#if highlight.type === 'goal' || highlight.type === 'opponent-goal'}
                <span class="highlight-goal-number">{highlight.goal_index}</span>
              {/if}
            </div>
            <div class="text text-sm">
              {@html getPlayerNames(highlight.actions, highlight.type)}
            </div>
          </div>
          {#if highlight.comment}
            <div class="highlight-comment">
              {highlight.comment}
            </div>
          {/if}
        </div>
      </button>

    </div>
    </article>
  {/each}
</div>

{#if highlights.length > 0}
  <hr class="my-6"/>
  <div class="align-right full-width mt-6">
    <button class="secondary ghost small " on:click={openResetConfirmation}>
      <ion-icon name="trash-outline"></ion-icon>
      {$_('scoreBoard.resetHighlights')}
    </button>
  </div>
{/if}

<!-- Menu Modal -->
{#if showMenuModal}
  <dialog open>
    <article on:click|stopPropagation>
      <header class="py-4">
        <h5>{$_('scoreBoard.menu.title')}</h5>
        <button class="close-button" on:click={closeMenuModal}>×</button>
      </header>
      <button class="secondary outline full-width mb-2" on:click={() => moveHighlight('up')}>
        <ion-icon name="arrow-up"></ion-icon>
        {$_('scoreBoard.menu.moveUp')}
      </button>
      <button class="secondary outline full-width mb-2" on:click={() => moveHighlight('down')}>
        <ion-icon name="arrow-down"></ion-icon>
        {$_('scoreBoard.menu.moveDown')}
      </button>
      <button class="secondary outline full-width mb-2" on:click={deleteHighlight}>
        <ion-icon name="trash-outline"></ion-icon>
        {$_('scoreBoard.menu.delete')}
      </button>
    </article>
  </dialog>
{/if}

<!-- Add Highlight Modal -->
{#if showHighlightModal}
  <dialog bind:this={highlightDialogElement} class="highlight-dialog slide-up-dialog">
    <article class="mt-0">
      <header class="py-4">
        <h5>{editingHighlight ? $_('scoreBoard.editHighlight') : $_('scoreBoard.addHighlight')}</h5>
        <button class="close-button" on:click={closeModals}>×</button>
      </header>
      <div class="highlight-dialog-content">
        {#if editingHighlight}
        {@const highlightType = highlightTypes.find(type => type.id === editingHighlight.type)}
        <div class="highlight-left text text-lg">
          <span class="highlight-icon">{highlightType.icon}</span>
          <span class="highlight-label">{highlightType.label} {editingHighlight.goal_index ? `${editingHighlight.goal_index}` : ''}</span>
        </div>
        {/if}
        <!-- Highlight Type Selection -->
        <div class="grid grid-2" class:is-hidden={editingHighlight}>
          {#each highlightTypes as type}
          {@const isSelected = selectedHighlightType?.id === type.id}
            {#if (!editingHighlight && !type.hidden ) || (editingHighlight && editingHighlight.type === type.id)}
            <button 
              disabled={editingHighlight && editingHighlight.type !== type.id}
              class="small mb-0"
              class:outline={!isSelected}
              class:contrast={isSelected}
              class:secondary={!isSelected}
              on:click={() => selectHighlightType(type)}
          >
            <span class="highlight-icon">{type.icon}</span>
                <span>{type.label}</span>
              </button>
            {/if}
          {/each}
        </div>

        <!-- Actions and Players (shown if highlight type is selected) -->
        {#if selectedHighlightType}
          <div class="actions-section">
            {#each selectedHighlightType.actions as action}
              {#if [...sessionInfo.active_team.team_members].length > 0}
                <CollapsibleSelect
                  label={$_('scoreBoard.actions.' + action)}
                  options={[...sessionInfo.active_team.team_members].sort((a, b) => a.name.localeCompare(b.name))}
                  selectedValue={getSelectedPlayer(editingHighlight, action)}
                  actionType={action}
                  on:select={(e) => handlePlayerSelect(e, action)}
                  on:clear={(e) => handlePlayerClear(e, action)}
                />
              {:else}
                <div class="action-group">
                  <h4>{$_('scoreBoard.actions.' + action)}</h4>
                  <p>{$_('scoreBoard.noPlayers')}</p>
                </div>
              {/if}
            {/each}

            <!-- <label for="highlight-comment">{$_('scoreBoard.comment')}</label> -->
            <!-- <button class="outline full-width mb-2">
              <ion-icon name="mic"></ion-icon>
              {$_('match.report.record')}
            </button> -->

            <textarea
              id="highlight-comment"
              rows="3"
              class="mt-0"
              placeholder={$_('scoreBoard.commentPlaceholder')}
              bind:value={currentComment}
            ></textarea>
            
          </div>
        {/if}
      </div>

      <footer class="sticky-footer">  
        <button class="full-width" on:click={closeModals}>
          {$_('common.save')}
        </button>
      </footer>

    </article>
  </dialog>
{/if}

<!-- Reset Confirmation Modal -->
{#if showResetConfirmation}
  <dialog open on:click={closeResetConfirmation}>
    <article on:click|stopPropagation>
      <header>
        <h3>{$_('scoreBoard.resetConfirmationTitle')}</h3>
      </header>
      <p>{$_('scoreBoard.resetConfirmationMessage')}</p>
      <footer class="flex space-between">
        <button class="secondary" on:click={closeResetConfirmation}>
          {$_('scoreBoard.cancel')}
        </button>
        <button on:click={resetHighlights}>
          {$_('scoreBoard.confirmReset')}
        </button>
      </footer>
    </article>
  </dialog>
{/if}

<style>
  .score-board {
    display: flex;
    align-items: center;
    gap: 1rem;
    width: 100%;
    margin-bottom: 2rem;
  }

  .score-board.away-game {
    flex-direction: row-reverse;
  }
  
  .team-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
  }

  .club-name {
    font-size: 0.75rem;
    text-align: center;
    color: var(--pico-muted-color);
    margin-bottom: 0.25rem;
  }

  .team-logo {
    width: 30px;
    height: 30px;
    object-fit: contain;
  }

  .team-name {
    margin-bottom: 0.5rem;
    font-size: 1rem;
    font-weight: normal;
    text-align: center;
    color: var(--color-text);
    min-height: 30px;
  }
  
  .score-display {
    font-size: 2.5rem;
    font-weight: bold;
    background-color: transparent !important;
    padding: 0.5rem;
    cursor: pointer;
    width: 6rem;
    text-align: center;
    line-height: 1;
    margin: .15rem 0 .15rem 0;
    color: var(--pico-contrast);
  }
  
  .score-divider {
    font-size: 4rem;
    font-weight: bold;
    margin: 0 1rem;
    padding-top: 3rem;
  }
  
  .score-controls {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  .score-adjust {
    width: 6rem;
    height: 3rem;
    font-size: 2rem;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: .15rem;
  }

  .highlights-list {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0rem;
  }

  .highlight-row {
    display: flex;
    align-items: stretch;
    width: 100%;
    background: transparent;
    border: none;
    overflow: hidden;
    border-radius: var(--pico-border-radius);
  }

  .highlight-main-btn {
    flex: 1;
    display: flex;
    padding: 0.75rem;
    color: var(--pico-secondary);
    background-color: transparent;
    border: none;
    box-shadow: none;
    cursor: pointer;
    transition: background-color 0.2s ease;
    text-align: left;
    margin: 0;
  }

  .highlight-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .highlight-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .highlight-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .highlight-icon {
    font-size: 1.2rem;
  }

  .highlight-label {
    font-weight: 600;
  }

  .highlight-goal-number {
    font-size: 0.9rem;
    color: var(--pico-muted-color);
  }

  .highlight-comment {
    font-size: 0.875rem;
    color: var(--pico-muted-color);
    font-style: italic;
    padding-top: 0.25rem;
    font-weight: 400;
  }

  .actions-section {
    margin-top: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .action-group {
    margin-bottom: 0;
  }

  .action-group h4 {
    margin-bottom: 1rem;
    color: var(--pico-primary-text);
    font-size: 1.1rem;
    text-transform: capitalize;
  }

  /* Consistent spacing for highlight type indicator */
  .highlight-dialog-content .highlight-left {
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
  }

  /* Ensure textarea has consistent top spacing */
  .actions-section textarea {
    margin-top: 0.5rem;
  }

  .highlight-menu-btn {
    width: 2rem;
    color: var(--pico-primary-text);
    background-color: var(--color-background);
    border: none;
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    margin: 0;
    padding-left: 0.5rem;
    padding-right: 0rem;
  }

  .highlight-menu-btn:hover {
    background-color: var(--color-background-hover);
  }

  .highlight-dialog article {
    display: flex;
    flex-direction: column;
    max-height: 90vh;
    overflow: hidden;
  }
  
  .highlight-dialog-content {
    flex: 1;
    overflow-y: auto;
    padding-bottom: 10px; /* Space for footer */
  }
  
  .sticky-footer {
    position: sticky;
    bottom: 0;
    background-color: var(--pico-background-color);
    padding: 1rem;
    border-top: 1px solid var(--pico-secondary-focus);
    margin: 0 -1rem -1rem -1rem;
    width: calc(100% + 2rem);
    z-index: 10;
  }

</style> 
